import "isomorphic-fetch";
import { API_BASE_URL } from '../config/api';
import { nodeConstants } from '../_constants';
import { fetchWithAuth } from '../_helpers/fetchWithAuth';
import { runCachedRequest } from '../_helpers/requestCache';

const API = API_BASE_URL;
const ROOT_CACHE_VERSION = 'v2';

let latestFetchId = 0;
let lastFetchedRegionId = undefined;

// Cache key: user- and region-scoped so different users don't share cached trees
function getCacheKey(userId, regionId) {
  return `nodes_cache_${ROOT_CACHE_VERSION}_${userId}_${regionId || 'all'}`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key, hash, nodes) {
  try {
    localStorage.setItem(key, JSON.stringify({ hash, nodes, cachedAt: Date.now() }));
  } catch {
    // localStorage might be full — ignore silently
  }
}

async function fetchHash(extraHeaders) {
  const response = await runCachedRequest(
    `nodes-hash:${extraHeaders['region-id'] || 'all'}`,
    () => fetchWithAuth(`${API}/nodes/hash`, {
      method: 'GET',
      headers: extraHeaders,
      cache: 'no-store',
    }),
  );
  if (!response.ok) throw new Error('Hash fetch failed');
  const ct = response.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new Error('Hash endpoint returned non-JSON (API not reachable?)');
  }
  return response.json(); // { hash, timestamp }
}

async function fetchAllNodesPaginated(extraHeaders, regionId) {
  let page = 1;
  const pageSize = 1000;
  let allNodes = [];
  let hasMore = true;

  while (hasMore) {
    const regionParam = regionId ? `&regionId=${regionId}` : '';
    const url = `${API}/nodes?page=${page}&pageSize=${pageSize}&includeAssets=false&includeMetadata=false&includeFiles=false${regionParam}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: extraHeaders, cache: 'no-store' });
    if (!response.ok) throw new Error(response.statusText);

    const data = await response.json();
    const nodes = data.entities || data;
    const total = data.total || 0;

    if (nodes.length === 0) {
      hasMore = false;
    } else {
      allNodes = allNodes.concat(nodes);
      hasMore = allNodes.length < total;
      page++;
    }
  }
  return allNodes;
}

// Fetch all nodes with stale-while-revalidate caching.
// 1. If a local cache exists: dispatch it immediately so the menu appears instantly.
// 2. Fetch the server hash in the background.
// 3. If the hash differs (or there was no cache), fetch fresh data, update cache + store.
export function fetchAllNodes(regionId = null) {
  return async (dispatch) => {
    const fetchId = ++latestFetchId;

    // Clear stale branch data from previous region immediately
    dispatch({ type: nodeConstants.NODE_CLEAR_BRANCHES });

    const isRegionChange = regionId !== lastFetchedRegionId;
    lastFetchedRegionId = regionId;

    const extraHeaders = {};
    if (regionId) extraHeaders['region-id'] = regionId;

    // Determine cache key from stored userId
    let userId = 'anon';
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      userId = storedUser.id || storedUser.userId || storedUser.sub || 'anon';
    } catch { /* ignore */ }

    const cacheKey = getCacheKey(userId, regionId);
    // Skip localStorage cache on region change — the backend hash endpoint is not
    // region-scoped and would otherwise always report a cache hit after the first visit.
    const cached = isRegionChange ? null : readCache(cacheKey);

    // ── Step 1: Serve from cache immediately (no loading spinner) ──────────
    if (cached && Array.isArray(cached.nodes) && cached.nodes.length > 0) {
      if (fetchId === latestFetchId) {
        dispatch({
          type: nodeConstants.NODE_FETCH_DATA_SUCCESS,
          nodes: cached.nodes,
          id: 'all',
        });
      }
    } else {
      dispatch({ type: nodeConstants.NODE_IS_LOADING, isLoading: true });
    }

    try {
      // ── Step 2: Check hash ───────────────────────────────────────────────
      const { hash: serverHash } = await fetchHash(extraHeaders);

      if (fetchId !== latestFetchId) return;

      if (cached && cached.hash === serverHash) {
        // Cache is fresh — nothing to do
        dispatch({ type: nodeConstants.NODE_IS_LOADING, isLoading: false });
        return cached.nodes;
      }

      // ── Step 3: Hash changed (or no cache) — fetch fresh data ───────────
      const freshNodes = await runCachedRequest(
        `nodes-roots:${regionId || 'all'}`,
        () => fetchAllNodesPaginated(extraHeaders, regionId),
      );

      if (fetchId !== latestFetchId) return;

      writeCache(cacheKey, serverHash, freshNodes);

      dispatch({
        type: nodeConstants.NODE_FETCH_DATA_SUCCESS,
        nodes: freshNodes,
        id: 'all',
      });
      dispatch({ type: nodeConstants.NODE_IS_LOADING, isLoading: false });

      return freshNodes;
    } catch (error) {
      if (fetchId !== latestFetchId) return;
      dispatch({ type: nodeConstants.NODE_HAS_ERRORED, hasErrored: true });
      dispatch({ type: nodeConstants.NODE_IS_LOADING, isLoading: false });
      throw error;
    }
  };
}
